/**
 * @fileoverview RawResponseViewer
 * @description Shows exactly what a checkpoint received, unedited.
 *
 *              Mapping arguments are settled against the payload, not against
 *              a normalized row: "AIMS is missing the deadhead flag" and "our
 *              path for it is wrong" look identical in the results table and
 *              completely different here. AIMS in particular omits an element
 *              entirely when it is empty, so seeing the absence is the answer.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton,
  InputAdornment, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Check, Copy, Download, Search, X } from 'lucide-react';

const NAVY = '#01426A';

/** Pretty-print JSON when it parses; otherwise show it as sent. */
function prettify(body, format) {
  if (format !== 'json') return body;
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}

const RawResponseViewer = ({ open, onClose, raw, title, detail }) => {
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState(false);

  const text = useMemo(() => prettify(raw?.body || '', raw?.format), [raw]);

  const lines = useMemo(() => {
    const all = text.split('\n');
    if (!query.trim()) return all.map((line, i) => ({ line, number: i + 1, hit: false }));
    const needle = query.toLowerCase();
    return all
      .map((line, i) => ({ line, number: i + 1, hit: line.toLowerCase().includes(needle) }))
      .filter((entry) => entry.hit);
  }, [text, query]);

  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const download = () => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${(title || 'response').replace(/\W+/g, '-').toLowerCase()}.${raw?.format || 'txt'}`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: '16px', height: '80vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" fontWeight={800} color={NAVY}>
                Raw response
              </Typography>
              {raw?.format && (
                <Chip size="small" label={raw.format.toUpperCase()}
                  sx={{ height: 20, fontWeight: 800, fontSize: '0.62rem', bgcolor: '#eff6ff', color: NAVY }} />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {title}{detail ? ` · ${detail}` : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            <Tooltip title={copied ? 'Copied' : 'Copy'}>
              <IconButton size="small" onClick={copy}>
                {copied ? <Check size={16} color="#00875A" /> : <Copy size={16} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton size="small" onClick={download}><Download size={16} /></IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: '#fbfdfe' }}>
        <TextField
          size="small" fullWidth placeholder="Filter lines…"
          value={query} onChange={(event) => setQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search size={15} color="#64748b" /></InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }}
        />

        {raw?.truncated && (
          <Alert severity="info" sx={{ borderRadius: '10px' }}>
            The response was longer than the viewer keeps; this is the first part of it.
          </Alert>
        )}

        <Box
          component="pre"
          sx={{
            flex: 1, m: 0, overflow: 'auto', borderRadius: '10px', border: '1px solid #e5e7eb',
            bgcolor: '#0f172a', color: '#e2e8f0', p: 1.5,
            fontFamily: 'monospace', fontSize: '0.72rem', lineHeight: 1.6,
          }}
        >
          {lines.map(({ line, number }) => (
            <Box key={number} component="div" sx={{ display: 'flex', gap: 1.5 }}>
              <Box component="span" sx={{ color: '#475569', userSelect: 'none', minWidth: 42, textAlign: 'right' }}>
                {number}
              </Box>
              <Box component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{line}</Box>
            </Box>
          ))}
          {!lines.length && (
            <Box component="span" sx={{ color: '#94a3b8' }}>No line matches that filter.</Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/** Button that opens the viewer, disabled when nothing was captured. */
export function RawResponseButton({ raw, title, detail, size = 'small', variant = 'text' }) {
  const [open, setOpen] = useState(false);
  if (!raw?.body) return null;
  return (
    <>
      <Button
        size={size} variant={variant} startIcon={<Search size={14} />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', fontWeight: 600, color: NAVY, whiteSpace: 'nowrap' }}
      >
        Raw response
      </Button>
      <RawResponseViewer open={open} onClose={() => setOpen(false)} raw={raw} title={title} detail={detail} />
    </>
  );
}

export default RawResponseViewer;
